import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, matches, highlights, articles, faqData, testimonials } from '@/mocks/sportsData';
import type { Match, Highlight, Article } from '@/mocks/sportsData';

const heroBgImage = 'https://readdy.ai/api/search-image?query=Dynamic%20wide%20angle%20sports%20stadium%20atmosphere%20with%20dramatic%20golden%20warm%20lighting%2C%20abstract%20artistic%20style%2C%20coral%20and%20amber%20color%20tones%2C%20modern%20sports%20broadcast%20aesthetic%2C%20atmospheric%20crowd%20energy%2C%20professional%20lens%20flare%20and%20soft%20gradients%2C%20high%20contrast%20dramatic%20shadows%2C%20epic%20sports%20photography%20composition&width=1800&height=1000&seq=hero-bg-sports-2026&orientation=landscape';

export default function Home() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const liveMatches: Match[] = matches.filter((m) => m.status === 'live');
  const upcomingMatches: Match[] = matches.filter((m) => m.status === 'upcoming');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9s99u7nahb7382ovsgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || text;
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '订阅失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEO
        title="每日大赛在线观看·高清直播全程不错过 | 每日大赛"
        description="每日大赛在线观看，汇聚全球顶级赛事直播资源，足球、篮球、电竞等多项目高清免费在线观看。实时赛程表、精彩集锦、赛后回放一站全覆盖，随时随地畅享每日大赛在线观看体验。"
        keywords="每日大赛在线观看,大赛直播在线观看,今日赛事直播,高清体育赛事直播,免费在线观看比赛,赛事直播平台,电竞大赛直播"
        canonicalUrl="https://www.meiri-dasai.com/"
      />
      <SchemaOrg type="WebSite" data={{ name: '每日大赛在线观看', url: 'https://www.meiri-dasai.com/', description: '汇聚全球顶级赛事直播资源，高清免费在线观看' }} />
      <SchemaOrg type="Organization" data={{ name: '每日大赛在线观看', url: 'https://www.meiri-dasai.com/', contactPoint: { '@type': 'ContactPoint', telephone: '400-888-9999', contactType: 'customer service', availableLanguage: 'Chinese' } }} />
      <SchemaOrg type="WebPage" data={{ name: '每日大赛在线观看 - 首页', url: 'https://www.meiri-dasai.com/', isPartOf: { '@type': 'WebSite', name: '每日大赛在线观看' } }} />
      <SchemaOrg type="FAQPage" data={{ mainEntity: faqData.map((faq) => ({ '@type': 'Question', name: faq.question, acceptedAnswer: { '@type': 'Answer', text: faq.answer } })) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img src={heroBgImage} alt="每日大赛在线观看 - 体育赛事直播平台" className="w-full h-full object-cover object-top" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          <div className="relative z-10 w-full text-center px-4 md:px-6">
            <div className="max-w-[800px] mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/20 border border-primary-400/30 mb-6">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                <span className="text-sm text-white/90">{liveMatches.length}场赛事正在直播中</span>
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-4">
                每日大赛在线观看
                <span className="block text-2xl md:text-3xl lg:text-4xl font-medium mt-2 text-white/90">高清直播全程不错过</span>
              </h1>
              <p className="text-base md:text-lg text-white/75 max-w-[600px] mx-auto mb-8 leading-relaxed">
                汇聚全球顶级<strong>赛事直播资源</strong>，足球、篮球、电竞等多项目<strong>高清免费在线观看</strong>。实时赛程表、精彩集锦、赛后回放一站全覆盖。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/category/football" className="px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer text-base">
                  <i className="ri-play-circle-line mr-2"></i>立即观看直播
                </Link>
                <Link to="/about" className="px-8 py-3 bg-white/15 text-white font-medium rounded-md hover:bg-white/25 transition-all duration-200 whitespace-nowrap cursor-pointer text-base backdrop-blur-sm">
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </header>

        <main>
          {/* Live Now Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    <span className="inline-block w-3 h-3 rounded-full bg-red-500 animate-pulse mr-3 align-middle"></span>
                    正在直播
                  </h2>
                  <p className="text-sm text-foreground-600 mt-1">实时高清直播，不错过每一个精彩瞬间</p>
                </div>
                <Link to="/category/football" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                  查看全部 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {liveMatches.map((match) => (
                  <MatchCard key={match.id} match={match} isLive />
                ))}
              </div>
            </div>
          </section>

          {/* Category Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">赛事项目</h2>
                <p className="text-sm text-foreground-600 mt-2">选择你感兴趣的运动项目，观看对应赛事直播</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="group bg-background-50 rounded-lg p-6 text-center hover:bg-primary-50 transition-all duration-200 cursor-pointer border border-background-200/70"
                  >
                    <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100/70 group-hover:bg-primary-200 transition-colors">
                      <i className={`${cat.icon} text-2xl text-primary-500`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-1">{cat.name}</h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">{cat.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Schedule Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">今日赛程</h2>
                  <p className="text-sm text-foreground-600 mt-1">2026年8月9日 赛事安排一览</p>
                </div>
              </div>
              <div className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                <div className="divide-y divide-background-200/70">
                  {[...liveMatches, ...upcomingMatches].slice(0, 8).map((match, idx) => (
                    <Link
                      key={match.id}
                      to={`/article/${match.id}`}
                      className="flex items-center justify-between px-5 py-4 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <span className="text-xs font-medium text-foreground-500 w-8 flex-shrink-0">{idx + 1}</span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">{match.league}</span>
                            {match.status === 'live' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">直播中</span>
                            )}
                            {match.status === 'upcoming' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">即将开始</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-sm font-semibold text-foreground-900">{match.teamA}</span>
                            {match.scoreA !== null ? (
                              <span className="text-lg font-heading font-bold text-primary-500">{match.scoreA} - {match.scoreB}</span>
                            ) : (
                              <span className="text-sm text-foreground-400">vs</span>
                            )}
                            <span className="text-sm font-semibold text-foreground-900">{match.teamB}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <span className="text-sm font-medium text-foreground-700">{match.time}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Highlights Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">精彩集锦</h2>
                  <p className="text-sm text-foreground-600 mt-1">赛事回放与高光时刻，随时重温经典</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {highlights.map((hl) => (
                  <HighlightCard key={hl.id} highlight={hl} />
                ))}
              </div>
            </div>
          </section>

          {/* Articles Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">赛事资讯</h2>
                  <p className="text-sm text-foreground-600 mt-1">深度赛事分析和最新动态报道</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {articles.slice(0, 3).map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">用户口碑</h2>
                <p className="text-sm text-foreground-600 mt-2">来自真实用户的使用评价</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {testimonials.map((t, idx) => (
                  <div key={idx} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                    <div className="flex gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-sm font-semibold text-primary-600">{t.name[0]}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-[900px] mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">常见问题</h2>
                <p className="text-sm text-foreground-600 mt-2">关于每日大赛在线观看的常见疑问</p>
              </div>
              <div className="space-y-3">
                {faqData.map((faq, idx) => (
                  <details key={idx} className="group bg-background-100 rounded-lg border border-background-200/70">
                    <summary className="px-5 py-4 cursor-pointer text-sm font-medium text-foreground-900 flex items-center justify-between list-none">
                      <span>{faq.question}</span>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-3">
                        <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Newsletter */}
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-[600px] mx-auto text-center">
              <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-mail-send-line text-2xl text-accent-500"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">订阅赛事资讯</h2>
              <p className="text-sm text-foreground-600 mb-6">第一时间获取赛事直播安排和独家分析，绝不错过任何精彩</p>
              {formStatus === 'success' ? (
                <div className="bg-green-50 border border-green-200 rounded-lg px-5 py-4 text-sm text-green-700">
                  <i className="ri-checkbox-circle-line mr-2"></i>订阅成功！感谢您的关注，我们会将最新的赛事资讯发送到您的邮箱。
                </div>
              ) : (
                <form onSubmit={handleSubscribe} data-readdy-form="" className="flex flex-col sm:flex-row gap-3 max-w-[460px] mx-auto">
                  <input type="email" name="email" required placeholder="输入您的邮箱地址" className="flex-1 px-4 py-3 text-sm bg-background-50 border border-background-300/60 rounded-md outline-none focus:border-primary-400 transition-colors" />
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                  <button type="submit" className="px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    立即订阅
                  </button>
                </form>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 mt-3">{formError}</p>
              )}
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}

function MatchCard({ match, isLive }: { match: Match; isLive?: boolean }) {
  return (
    <Link to={`/article/${match.id}`} className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer">
      <div className="relative h-44 overflow-hidden">
        <img src={match.imageUrl} alt={`${match.teamA} vs ${match.teamB}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {isLive && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500 text-white text-xs font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
            直播中
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">{match.league}</span>
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-foreground-900">{match.teamA}</span>
          {match.scoreA !== null ? (
            <span className="text-lg font-heading font-bold text-primary-500">{match.scoreA} - {match.scoreB}</span>
          ) : (
            <span className="text-xs text-foreground-400">VS</span>
          )}
          <span className="text-sm font-semibold text-foreground-900">{match.teamB}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-500">{match.title}</span>
          <span className="text-xs font-medium text-foreground-600">{match.time}</span>
        </div>
      </div>
    </Link>
  );
}

function HighlightCard({ highlight }: { highlight: Highlight }) {
  return (
    <Link to={`/article/${highlight.matchId}`} className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer">
      <div className="relative h-44 overflow-hidden">
        <img src={highlight.imageUrl} alt={highlight.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
        <div className="absolute bottom-3 right-3 px-2 py-1 rounded bg-black/65 text-white text-xs font-medium">
          <i className="ri-play-fill mr-0.5"></i>{highlight.duration}
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-medium text-foreground-900 leading-snug mb-2 line-clamp-2">{highlight.title}</h3>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span><i className="ri-eye-line mr-1"></i>{highlight.views}</span>
          <span>{highlight.date}</span>
        </div>
      </div>
    </Link>
  );
}

function ArticleCard({ article }: { article: Article }) {
  return (
    <Link to={`/article/${article.id}`} className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer">
      <div className="h-44 overflow-hidden">
        <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-4">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {article.tags.map((tag) => (
            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">{tag}</span>
          ))}
        </div>
        <h3 className="text-sm font-medium text-foreground-900 leading-snug mb-2 line-clamp-2">{article.title}</h3>
        <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{article.date}</span>
          <span><i className="ri-time-line mr-1"></i>{article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}